import React, { useMemo, useRef, useState } from 'react'
import { Modal, Button } from 'antd'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import AddInvoices from 'src/components/AddInvoices'
import { GR_ADD_INVOICE_BUTTON_LABEL } from 'src/utils/goodsReceiptingMatchHelpers'
import { buildInvoicesUploadPath } from 'src/utils/reconciliationHealth'
import { mapInvoiceToTableRow } from 'src/utils/invoiceUploadHelpers'
import { findStrongLibraryDuplicateForGrUpload } from 'src/utils/goodsReceiptingUploadDuplicateHelpers'

/**
 * GR Upload new — same AddInvoices/OCR product, with soft duplicate nudge
 * toward Link existing when the library already has this supplier + invoice #.
 */
const GrUploadInvoiceModal = ({
  visible,
  venueId,
  order,
  onCancel,
  onUploaded,
  onUseExistingInvoice,
}) => {
  const uploadedInvoices = useSelector(
    state => state.newInvoices.uploadedInvoices,
  )
  const invoiceRows = useMemo(
    () => (uploadedInvoices || []).map(mapInvoiceToTableRow),
    [uploadedInvoices],
  )

  const [duplicateNudge, setDuplicateNudge] = useState(null)
  const nudgeResolverRef = useRef(null)

  const uploadPrefill = useMemo(() => {
    if (!order) return null
    return {
      upload: true,
      po: String(order.purchaseOrderNumber || '').trim(),
      supplierId: String(order.venueSupplier?._id || '').trim(),
      supplierTitle: String(order.supplier?.title || '').trim(),
    }
  }, [order])

  const invoicesTabPath = useMemo(() => {
    if (!venueId || !order) return null
    return buildInvoicesUploadPath(venueId, {
      po: order.purchaseOrderNumber,
      supplierId: order.venueSupplier?._id,
      supplierTitle: order.supplier?.title,
    })
  }, [venueId, order])

  const closeNudge = () => {
    setDuplicateNudge(null)
  }

  const resolveNudge = decision => {
    const resolve = nudgeResolverRef.current
    nudgeResolverRef.current = null
    closeNudge()
    if (typeof resolve === 'function') resolve(decision)
  }

  const handleBeforeConfirmUpload = async selectedFiles => {
    const hit = findStrongLibraryDuplicateForGrUpload({
      uploadFiles: selectedFiles,
      libraryInvoices: invoiceRows,
      order,
    })
    if (!hit) return true

    return new Promise(resolve => {
      nudgeResolverRef.current = resolve
      setDuplicateNudge(hit)
    })
  }

  const handleUploadAnyway = () => {
    resolveNudge(true)
  }

  const handleUseExisting = () => {
    const invoice = duplicateNudge?.existingInvoice
    resolveNudge('use_existing')
    // Let AddInvoices finish discarding the draft upload before we unmount it.
    window.setTimeout(() => {
      if (invoice) onUseExistingInvoice?.(invoice)
    }, 250)
  }

  if (!order) return null

  const existingNumber =
    duplicateNudge?.invoiceNumber ||
    duplicateNudge?.existingInvoice?.invoiceNumber ||
    ''

  return (
    <>
      <StyledModal
        title={GR_ADD_INVOICE_BUTTON_LABEL}
        visible={visible}
        onCancel={onCancel}
        footer={<Button onClick={onCancel}>Close</Button>}
        destroyOnClose
        width={520}
      >
        <Lead>
          Same upload as <strong>Add Invoices / Credit Notes</strong> on the
          Invoices tab — pick the file, check what we read, confirm. Multi-page
          scans stay one invoice; separate files stay separate invoices. Changes
          here show on Invoices (and the other way around).
        </Lead>
        {uploadPrefill?.po ? (
          <ContextLine>
            Prefilling PO <strong>{uploadPrefill.po}</strong>
            {uploadPrefill.supplierTitle
              ? ` · ${uploadPrefill.supplierTitle}`
              : ''}{' '}
            when the invoice leaves those blank.
          </ContextLine>
        ) : null}
        <AddInvoices
          venueId={venueId}
          uploadPrefill={uploadPrefill}
          triggerLabel={GR_ADD_INVOICE_BUTTON_LABEL}
          onConfirmed={onUploaded}
          beforeConfirmUpload={handleBeforeConfirmUpload}
        />
        {invoicesTabPath ? (
          <AltLink>
            Prefer the full page?{' '}
            <Link to={invoicesTabPath} onClick={onCancel}>
              Open Add Invoices / Credit Notes
            </Link>
          </AltLink>
        ) : null}
      </StyledModal>

      <Modal
        title='Possible duplicate invoice'
        visible={Boolean(duplicateNudge)}
        onCancel={handleUploadAnyway}
        destroyOnClose
        footer={
          <FooterRow>
            <Button onClick={handleUploadAnyway}>Upload anyway</Button>
            <Button type='primary' onClick={handleUseExisting}>
              Use existing instead
            </Button>
          </FooterRow>
        }
        width={480}
      >
        <Lead>
          An invoice with number{' '}
          <strong>
            {existingNumber ? `#${existingNumber}` : 'this number'}
          </strong>{' '}
          is already in Invoices &amp; Credit Notes for{' '}
          <strong>{order.supplier?.title || 'this supplier'}</strong>. Linking
          the existing file avoids a duplicate.
        </Lead>
        <Hint>
          Soft warning only — you can still upload anyway if this really is a
          new document.
        </Hint>
      </Modal>
    </>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: 12px;
  }
`

const Lead = styled.p`
  margin: 0 0 12px;
  color: #595959;
  line-height: 1.5;
`

const ContextLine = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.4;
`

const AltLink = styled.p`
  margin: 16px 0 0;
  font-size: 13px;
  color: #8c8c8c;
`

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.45;
`

const FooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

export default GrUploadInvoiceModal
